import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import { useCurrentUser } from "components/context/user/UserContext"
import React from "react"
import { reportErrorMessage } from "utils/ErrorReporting"

export enum SupportedLocale {
  English_UnitedStates = "en-US",
  Spanish_UnitedStates = "es-US",
  French_Canada = "fr-CA",
  German_Germany = "de-DE",
  Portuguese_Brazil = "pt-BR",
  Hindi_India = "hi-IN"
}

export enum PseudoLocale {
  /* pseudo-locales
    see https://formatjs.io/docs/tooling/cli/#--pseudo-locale-pseudolocale
    and https://github.com/formatjs/formatjs/blob/main/packages/cli-lib/src/pseudo_locale.ts
  */
  Elongated = "xx-LS",
  Accented = "en-XA",
  Bidirectional = "en-XB"
}

const pseudoLocales: Array<PseudoLocale> = Object.values(PseudoLocale)

function isPseudoLocale(locale: SupportedLocale | PseudoLocale): locale is PseudoLocale {
  return pseudoLocales.includes(locale as PseudoLocale)
}

const displayNamesBySupportedLocale = {
  [SupportedLocale.English_UnitedStates]: "English (U.S.)",
  [SupportedLocale.Spanish_UnitedStates]: "Español (EE. UU.)",
  [SupportedLocale.French_Canada]: "Français (Canada)",
  [SupportedLocale.German_Germany]: "Deutsch (Deutschland)",
  [SupportedLocale.Portuguese_Brazil]: "Português (Brasil)",
  [SupportedLocale.Hindi_India]: "हिन्दी (भारत)"
} as const

export const defaultLocale = SupportedLocale.English_UnitedStates

export function getSupportedLocaleDisplayName(locale: SupportedLocale): string {
  return displayNamesBySupportedLocale[locale]
}

type CompiledMessage = {
  type: number
  value: string
}

type MessageId = string
type CompiledMessages = Record<MessageId, CompiledMessage[]>

// TODO add tests for this
function resolveLocaleMessageFile(
  locale: SupportedLocale | PseudoLocale,
  allowPseudoLocales: boolean
): `${string}.json` {
  if (allowPseudoLocales && isPseudoLocale(locale)) {
    return `${locale}.json`
  }

  switch (locale) {
    case SupportedLocale.Spanish_UnitedStates:
      return "es-US.json"
    case SupportedLocale.French_Canada:
      return "fr-CA.json"
    case SupportedLocale.German_Germany:
      return "de-DE.json"
    case SupportedLocale.Portuguese_Brazil:
      return "pt-BR.json"
    case SupportedLocale.Hindi_India:
      return "hi-IN.json"
    case SupportedLocale.English_UnitedStates:
    default:
      return "en-US.json"
  }
}

function loadLocaleMessages(locale: SupportedLocale, allowPseudoLocales: boolean): Promise<CompiledMessages> {
  const messageFile = resolveLocaleMessageFile(locale, allowPseudoLocales)
  return import(`compiledLang/${messageFile}`)
}

export function useIntlMessages(): { didCompiledMessagesLoad: boolean } {
  const { preferredLocale, setCompiledMessages } = usePreferredLocaleContext()
  const currentUser = useCurrentUser()
  const [didCompiledMessagesLoad, setDidCompiledMessagesLoad] = React.useState(false)

  React.useEffect(() => {
    async function getLocaleData(): Promise<void> {
      try {
        const allowPseudoLocales = currentUser?.isTestUser
        const messages = await loadLocaleMessages(preferredLocale, allowPseudoLocales)

        setCompiledMessages(messages)
        setDidCompiledMessagesLoad(true)
      } catch (err) {
        reportErrorMessage("Could not load locale messages")
      }
    }
    getLocaleData()
  }, [preferredLocale, setCompiledMessages, currentUser])

  return { didCompiledMessagesLoad }
}

export function getSupportedLocales(countryCode: string): SupportedLocale[] {
  switch (countryCode) {
    case "US":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.Spanish_UnitedStates]
    case "CA":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.French_Canada]
    case "MX":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.Spanish_UnitedStates]
    case "AR":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.Spanish_UnitedStates]
    case "DE":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.German_Germany]
    case "CH":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.German_Germany, SupportedLocale.French_Canada]
    case "BR":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.Portuguese_Brazil]
    case "IN":
      return [SupportedLocale.English_UnitedStates, SupportedLocale.Hindi_India]
    default:
      return [SupportedLocale.English_UnitedStates]
  }
}
